'use client';

import { useEffect, useRef } from 'react';

import { getLogger } from '@/logger/logger';
import { setAuthTokens } from '@/storage/auth-tokens.storage';
import { initializeAuth } from '@/utils/auth/init.util';

const logger = getLogger('auth-handoff');

const MAX_LOOP_COUNT = 3;

const ALLOWED_DOMAINS = [
  'medprev.online',
  'medprev.app',
  'localhost',
];

function isValidTokenFormat(token: string): boolean {
  return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token);
}

function isValidOrigin(url: string, allowedDomains: string[]): boolean {
  try {
    const { hostname } = new URL(url);
    return allowedDomains.some((domain) =>
      hostname === domain || hostname.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
}

export function useAuthHandoff() {
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;

    const params = new URLSearchParams(window.location.search);
    const isHandoff = params.get('_handoff') === '1' || params.get('from') === 'legacy';
    const token = params.get('token');

    if (isHandoff && token) {
      logger.info('Auth handoff detected', {
        format: params.get('_handoff') === '1' ? 'new' : 'legacy',
      });

      const loopCount = parseInt(params.get('_loop') || '0', 10);

      if (loopCount > MAX_LOOP_COUNT) {
        logger.error('Redirect loop detected', { loopCount, maxAllowed: MAX_LOOP_COUNT });
        params.delete('token');
        params.delete('_handoff');
        params.delete('_loop');
        params.delete('from');
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
        initializeAuth();
        return;
      }

      if (!isValidTokenFormat(token)) {
        logger.error('Invalid token format');
        params.delete('token');
        params.delete('_handoff');
        params.delete('_loop');
        params.delete('from');
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
        initializeAuth();
        return;
      }

      const { referrer } = document;
      if (referrer && !isValidOrigin(referrer, ALLOWED_DOMAINS)) {
        logger.error('Invalid referrer origin', { referrer, allowedDomains: ALLOWED_DOMAINS });
        params.delete('token');
        params.delete('_handoff');
        params.delete('_loop');
        params.delete('from');
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
        initializeAuth();
        return;
      }

      logger.info('Token received and storing');

      setAuthTokens({
        access_token: token,
        refresh_token: '',
      });

      params.delete('token');
      params.delete('_handoff');
      params.delete('_loop');
      params.delete('from');

      const cleanSearch = params.toString();
      const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '');

      window.history.replaceState({}, '', cleanUrl);

      logger.info('URL cleaned, initializing auth');
      initializeAuth();
      return;
    }

    logger.info('Starting auth initialization');
    initializeAuth();
  }, []);
}
